import React, { useReducer } from 'react';

import { enableAllPlugins, produce } from 'immer';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import useWindowStorage from 'components/hooks/useWindowStorage';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import DatePeriod from 'components/trainingRegisterReports/components/DatePeriod';
import {
  buildFormValues,
  validateForm,
} from 'components/trainingRegisterReports/helpers';
import useReportForm, {
  initialDateState,
  initialEstimatesState,
  initialFiltersState,
} from 'components/trainingRegisterReports/hooks/useReportForm';
import Report from 'components/trainingRegisterReports/reports/Report';
import { expiryDateSchema } from 'components/trainingRegisterReports/schemas/dateSchema';
import validationErrorsReducer, {
  initialValidationErrorsState,
  mapValidationErrorsDispatch,
} from 'components/validationErrors/reducers/validationErrorsReducer';

enableAllPlugins();

const initialFormValues = {
  expiryDate: initialDateState,
  estimates: initialEstimatesState,
  filters: initialFiltersState,
};

const additionalValidators = [{ schema: expiryDateSchema, key: 'expiryDate' }];

export default function ExpiringTrainingReport() {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.reports.ExpiringTrainingReport',
  });

  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.helpers',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem] =
    useWindowStorage(
      `expiringTrainingReport|${currentActor.user.id}|${currentActor.division.id}`,
    );

  const {
    formValues,
    handleDateChange,
    handleDateRangeChange,
    handleEstimatesChange,
    handleFilterAddition,
    handleFilterChange,
    handleFilterDeselection,
    handleFilterRemoval,
    handleFilterSelection,
  } = useReportForm('expiringTraining', initialFormValues, (initialState) => {
    const computedFormValues = produce(initialState, (draftState) => {
      draftState.expiryDate.period = 'withinTheNext';
      draftState.expiryDate.withinTheNextFrequency = '12';
      draftState.expiryDate.withinTheNextInterval = 'weeks';
      draftState.expiryDate.withinTheLastFrequency = '12';
      draftState.expiryDate.withinTheLastInterval = 'weeks';
      draftState.expiryDate.before = moment().toDate();
      draftState.expiryDate.after = moment().toDate();
      draftState.expiryDate.onExactDate = moment().toDate();
      draftState.expiryDate.customDateRangeFrom = moment().toDate();
      draftState.expiryDate.customDateRangeTo = moment()
        .add(3, 'months')
        .toDate();
    });

    return buildFormValues({
      type: 'expiringTraining',
      formValues: computedFormValues,
      additionalValidators,
      getLocalStorageItem,
      removeLocalStorageItem,
    });
  });

  const [validationErrors, validationErrorsDispatch] = useReducer(
    validationErrorsReducer,
    initialValidationErrorsState,
  );

  const validationErrorsHandlers = mapValidationErrorsDispatch(
    validationErrorsDispatch,
  );

  const handleValidate = () =>
    validateForm({
      formValues,
      additionalValidators,
      safe: true,
      translations: i18nTHelpers('validateForm', { returnObjects: true }),
    });

  return (
    <Report
      formValues={formValues}
      onEstimatesChange={handleEstimatesChange}
      onFilterAddition={handleFilterAddition}
      onFilterChange={handleFilterChange}
      onFilterDeselection={handleFilterDeselection}
      onFilterRemoval={handleFilterRemoval}
      onFilterSelection={handleFilterSelection}
      onValidate={handleValidate}
      onValidationErrorsAssignment={
        validationErrorsHandlers.handleValidationErrorsAssignment
      }
      onValidationErrorsReset={
        validationErrorsHandlers.handleValidationErrorsReset
      }
      onValidationErrorsUnhighlight={
        validationErrorsHandlers.handleValidationErrorsUnhighlight
      }
      setLocalStorageItem={setLocalStorageItem}
      title={i18nT('title')}
      tooltip={i18nT('tooltip')}
      type='expiringTraining'
      validationErrors={validationErrors}
    >
      <label className='tw-mb-1 tw-font-medium'>
        {i18nTShared('expiryDate')}
      </label>
      <div className='tw-mb-6'>
        <DatePeriod
          datePeriod={formValues.expiryDate}
          fieldError={validationErrors.expiryDate}
          name='expiry_date'
          onChange={handleDateChange}
          onDateRangeChange={handleDateRangeChange}
          onFocus={() => {
            if (validationErrors.expiryDate)
              validationErrorsHandlers.handleValidationErrorsUnhighlight(
                'expiryDate',
              );
          }}
        />
      </div>
    </Report>
  );
}
